import React from 'react';
import { Document } from './types';

function DocumentCategoryBadge({ category }: { category: { id: number; name: string } }) {
  return (
    <span className="badge themed-badge-gray themed-badge-gray--dotted">{category.name}</span>
  );
}

interface WorkflowBadgeProps {
  workflow: {
    id: number | null;
    name: string;
    displayName: string;
  };
}

function DocumentWorkflowBadge({ workflow }: WorkflowBadgeProps) {
  return (
    <span className="badge themed-badge-info themed-badge-info--dotted">{workflow.displayName}</span>
  );
}

function BadgeSeparator() {
  return (
    <span
      className="align-self-center"
      style={{
        width: '1px',
        height: '1rem',
        backgroundColor: 'currentColor',
        opacity: 0.3,
        margin: '0 0.25rem',
      }}
      aria-hidden="true"
    />
  );
}

function DocumentBadges({ document }: { document: Document }) {
  const { category, workflow } = document;
  const hasBoth = workflow && category;

  return (
    <div className="d-flex flex-wrap align-items-center gap-2">
      {workflow && (
        <>
          <DocumentWorkflowBadge workflow={workflow as WorkflowBadgeProps['workflow']} />
          {hasBoth && <BadgeSeparator />}
        </>
      )}
      {category && (
        <DocumentCategoryBadge category={category} />
      )}
    </div>
  );
}

function DocumentOriginBadge({ origin }: { origin: Document['origin'] }) {
  if (!origin) {
    return null;
  }
  return (
    <span className="badge rounded-pill bg-info fw-semibold fs-xs">{origin.label}</span>
  );
}

function DocumentName({ document }: { document: Document }) {
  return (
    <span className="hoverable-link">{document.displayName}</span>
  );
}
function DocumentInformation({ document }: { document: Document }) {
  return (
    <div className="d-flex flex-column align-items-start gap-3 position-relative">
      <DocumentBadges document={document} />
      <DocumentName document={document} />
    </div>
  );
}

export function DocumentsTable({ documents }: { documents: Document[] }) {
  return (
    <table className="w-100 links-table">
      <tbody>
        {documents.map((document) => {
          const hasOrigin = document.origin;
          return (
            <tr
              key={document.id}
              className="border-bottom border-gray-200 "
              data-link={hasOrigin ? 'true' : 'false'}
              onClick={hasOrigin ? () => window.open(document.origin?.url, '_blank') : undefined}
            >
              <td width="100%" className="py-4">
                <DocumentInformation document={document} />
              </td>
              <td className="pr-5">
                <DocumentOriginBadge origin={document.origin} />
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
